/* eslint-disable no-restricted-syntax, guard-for-in */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { reduxForm, FieldArray, getFormValues, change, isDirty } from 'redux-form';
import { Promise as BluebirdPromise } from 'bluebird';
import { get, orderBy, keyBy, isEqual } from 'lodash';
import moment from 'moment';
import {
    putCompany,
    getManyLearningAssets,
    getManyCollections,
    getLearningAssetStatus,
    getManyLearningAssetsAssociatedCollections,
    getManyAdminPathways
} from '../../../../../shared/actions/index';
import { makeGetObjectsFromStateById, makeGetFilteredObjectsFromState } from '../../../../../shared/selectors/index';
import FormFeedback from '../../../../../shared/components/formComponents/FormFeedback';
import LearningAssetLicensesTable from './LearningAssetLicensesTable';
import CollectionLicensesSection from './CollectionLicensesSection';
import RecommendedCollectionsSection from './RecommendedCollectionsSection';
import HiddenLearningAssetsSection from './HiddenLearningAssetsSection';
import { LICENSE_TYPES } from '../../../../../shared/Constants';
import VerbButton, { VERB_BUTTON_TYPE } from '../../../../../shared/components/VerbButton';
import { RuntimeContext } from '../../../../../../common/RuntimeContext';

class CompanyContentForm extends Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedLearningAssetId: '',
            selectedLearningAsset: {},
            company: {},
            associatedCollectionsByTrackId: {},
            trackLicensesByLicencedCollectionId: {}
        };
    }

    static propTypes = {
        company: PropTypes.object.isRequired,
        handleSubmit: PropTypes.func.isRequired,
        submitSucceeded: PropTypes.bool,
        submitFailed: PropTypes.bool,
        error: PropTypes.string,
        putCompany: PropTypes.func.isRequired,
        onCancel: PropTypes.func,
        submitting: PropTypes.bool,
        getManyLearningAssets: PropTypes.func.isRequired,
        getManyCollections: PropTypes.func.isRequired,
        getLearningAssetStatus: PropTypes.func.isRequired,
        getManyLearningAssetsAssociatedCollections: PropTypes.func.isRequired,
        getManyAdminPathways: PropTypes.func.isRequired,
        availableLearningAssets: PropTypes.array,
        availableCollections: PropTypes.array,
        availablePathways: PropTypes.array,
        currentValues: PropTypes.object,
        changeFormValue: PropTypes.func,
        learningAssetsById: PropTypes.object,
        collectionsById: PropTypes.object,
        pathwaysById: PropTypes.object,
        loggedInUser: PropTypes.object,
        isVerbAdmin: PropTypes.bool,
        isCompanyAdmin: PropTypes.bool,
        isInstructionalAdmin: PropTypes.bool,
        isAccountOwner: PropTypes.bool,
        loggedInUserId: PropTypes.string,
        pathwaysEnabled: PropTypes.bool,
        dirty: PropTypes.bool,
        toggleShowUnsavedChangesPrompt: PropTypes.func
    };

    static contextType = RuntimeContext

    componentDidMount() {
        const { getManyLearningAssets, getManyCollections, company, getManyLearningAssetsAssociatedCollections } =
            this.props;

        this.setState({
            company
        });

        const promises = [
            getManyLearningAssets({ partial: true, pathsContext: this.context.pathsWeb }),
            getManyCollections({pathsContext: this.context.pathsWeb}),
            getManyLearningAssetsAssociatedCollections(
                this.context.pathsWeb,
                (company.trackLicenses || []).map((trackLicense) => trackLicense.trackId)
            ).then((associatedCollectionsByTrackId) => {
                this.setState({
                    associatedCollectionsByTrackId,
                    trackLicensesByLicencedCollectionId: this._makeTrackLicensesByLicencedCollectionId(
                        associatedCollectionsByTrackId,
                        company.collectionLicenses
                    )
                });
            })
        ];

        return BluebirdPromise.all(promises);
    }

    componentDidUpdate(prevProps) {
        const { currentValues } = this.props;
        const { associatedCollectionsByTrackId } = this.state;

        if (!isEqual(currentValues.collectionLicenses, prevProps.currentValues.collectionLicenses)) {
            return this.setState({
                trackLicensesByLicencedCollectionId: this._makeTrackLicensesByLicencedCollectionId(
                    associatedCollectionsByTrackId,
                    currentValues.collectionLicenses
                )
            });
        }
        if (prevProps.dirty !== this.props.dirty) {
            return this.props.toggleShowUnsavedChangesPrompt(this.props.dirty);
        }
        return null;
    }

    /**
     *
     * @param {Object} associatedCollectionsByTrackId
     * @param {Array} collectionLicenses
     * @private
     * create a 2 level dictionary [collectionId] -> [trackId] -> track's associated Collections within company licenses
     * this lookup is used to remove associated licensed LearningAssets when a licensed Collections is removed
     */
    _makeTrackLicensesByLicencedCollectionId = (associatedCollectionsByTrackId, collectionLicenses) => {
        const lookup = {};
        const collectionLicensesByCollectionId = keyBy(collectionLicenses || [], 'collectionId');

        for (const trackId in associatedCollectionsByTrackId) {
            const currentAssociatedCollections = associatedCollectionsByTrackId[trackId];
            (currentAssociatedCollections || []).forEach((collection) => {
                if (collectionLicensesByCollectionId[collection._id]) {
                    lookup[collection._id] = {
                        ...lookup[collection._id],
                        [trackId]: currentAssociatedCollections.filter(
                            (collection) => !!collectionLicensesByCollectionId[collection._id]
                        )
                    };
                }
            });
        }
        return lookup;
    };

    getLicenseOptions = (allOptions, type) => {
        if (!allOptions) {
            return null;
        }
        const {
            currentValues: { collectionLicenses = [], pathwayLicenses = [] }
        } = this.props;
        let licenses = collectionLicenses;
        let idField = 'collectionId';
        if (type === 'pathway') {
            licenses = pathwayLicenses;
            idField = 'pathwayId';
        }

        const licensedIds = licenses.reduce((ids, licenseObj) => {
            ids[licenseObj[idField]] = true;
            return ids;
        }, {});

        const sortedOptions = allOptions.filter((licenseObj) => !licensedIds[licenseObj._id] && !licenseObj.isArchived);

        const nameAccessor = (item) => (item.name && item.name.toLocaleUpperCase()) || '';

        return orderBy(sortedOptions, nameAccessor, 'asc');
    };

    getPathwayOptionsList = (allOptions) => {
        if (!allOptions) {
            return null;
        }

        const {
            currentValues: { pathwayOptions = [], pathwayLicenses = [] }
        } = this.props;

        const licensedIds = pathwayLicenses.reduce((ids, licenseObj) => {
            ids[licenseObj.pathwayId] = true;
            return ids;
        }, {});

        const sortedOptions = allOptions.filter((pathway) => {
            // filter out pathways already in options list
            const pathwayIdxInOptions = pathwayOptions.findIndex((option) => option.id === pathway._id);

            // only show pathways available to all or company exclusive pathways
            const isPathwayAvailable = pathway.subscriptionType === 'AvailableForAll' ? true : licensedIds[pathway._id];
            return isPathwayAvailable && pathwayIdxInOptions < 0 && !pathway.isArchived;
        });

        const nameAccessor = (item) => (item.name && item.name.toLocaleUpperCase()) || '';

        return orderBy(sortedOptions, nameAccessor, 'asc');
    };

    getDefaultOptions = (allOptions) => {
        if (!allOptions) {
            return null;
        }

        const {
            currentValues: { defaultCollections = [], recommendedCollections = [] }
        } = this.props;

        const unavailableCollectionIds = {};
        defaultCollections.forEach((collection) => {
            unavailableCollectionIds[collection.id] = true;
        });
        recommendedCollections.forEach((collection) => {
            unavailableCollectionIds[collection.id] = true;
        });

        const sortedOptions = allOptions.filter((collection) => !unavailableCollectionIds[collection._id]);
        const nameAccessor = (item) => (item.name && item.name.toLocaleUpperCase()) || '';

        return orderBy(sortedOptions, nameAccessor, 'asc');
    };

    getCollectionOptionsForCompany = (allOptions) => {
        const {
            currentValues: {
                defaultCollections = [],
                collectionLicenses = [],
                recommendedCollections = [],
                hiddenCollections = []
            }
        } = this.props;

        const availableOptions = {};
        const licenseDict = collectionLicenses.reduce((acc, collection) => {
            acc[collection.collectionId] = true;
            return acc;
        }, {});

        allOptions.forEach((collection) => {
            if (collection.subscriptionType === 'AvailableForAll' || licenseDict[collection._id]) {
                availableOptions[collection._id] = {
                    _id: collection._id,
                    name: collection.name
                };
            }
        });

        defaultCollections.forEach((collection) => {
            delete availableOptions[collection.id];
        });
        recommendedCollections.forEach((collection) => {
            delete availableOptions[collection.id];
        });
        hiddenCollections.forEach((collection) => {
            delete availableOptions[collection.id];
        });

        const sortedOptions = Object.values(availableOptions);

        const nameAccessor = (item) => (item.name && item.name.toLocaleUpperCase()) || '';

        return orderBy(sortedOptions, nameAccessor, 'asc');
    };

    getHiddenAssetOptionsForCompany = (allOptions) => {
        const {
            currentValues: { trackLicenses = [], hiddenLearningAssets = [] }
        } = this.props;

        const availableOptions = {};
        trackLicenses.forEach((asset) => {
            availableOptions[asset.trackId] = {
                _id: asset.trackId,
                name: asset.trackName
            };
        });
        allOptions.forEach((asset) => {
            if (asset.subscriptionType === 'AvailableForAll') {
                availableOptions[asset._id] = {
                    _id: asset._id,
                    name: asset.name
                };
            }
        });

        hiddenLearningAssets.forEach((asset) => {
            delete availableOptions[asset.id];
        });

        const sortedOptions = Object.values(availableOptions);

        const nameAccessor = (item) => (item.name && item.name.toLocaleUpperCase()) || '';

        return orderBy(sortedOptions, nameAccessor, 'asc');
    };

    /**
     *
     * @param {Object} selectedObject
     * @private
     */
    _handleLearningAssetChange = (selectedObject) => {
        this.setState({
            selectedLearningAssetId: get(selectedObject, '_id', ''),
            selectedLearningAsset: selectedObject
        });
    };

    /**
     *
     * @param {Array} collectionContent
     */
    updateCompanyExclusiveLearningAssetLicenses = (collectionContent = []) => {
        const { learningAssetsById, currentValues, changeFormValue } = this.props;

        const existingLicensesById = (currentValues.trackLicenses || []).reduce((acc, curr) => {
            acc[curr.trackId] = curr;
            return acc;
        }, {});

        const contentLength = (collectionContent || []).length;
        let currentContentItem;
        const learningAssetsToAdd = [];
        for (let i = 0; i < contentLength; i += 1) {
            currentContentItem = collectionContent[i] && learningAssetsById[collectionContent[i].id];
            if (
                currentContentItem &&
                (currentContentItem.subscriptionType === LICENSE_TYPES.EXCLUSIVE_COMPANY_LICENSE ||
                    (currentValues.lockUnlicensed &&
                        currentContentItem.subscriptionType === LICENSE_TYPES.AVAILABLE_FOR_ALL))
            ) {
                if (!(currentContentItem._id in existingLicensesById)) {
                    learningAssetsToAdd.push({
                        trackId: currentContentItem._id,
                        trackName: currentContentItem.name,
                        licensedDateTime: moment.utc()
                    });
                }
            }
        }

        if (learningAssetsToAdd.length) {
            changeFormValue({
                field: 'trackLicenses',
                value: (currentValues.trackLicenses || []).concat(learningAssetsToAdd)
            });
        }
    };

    handleAssociatedCollectionsFetching = (learningAssetId) => {
        const { associatedCollectionsByTrackId } = this.state;
        if (!associatedCollectionsByTrackId[learningAssetId]) {
            return this.props.getLearningAssetStatus(learningAssetId, {pathsContext: this.context.pathsWeb}).then((learningAssetStatus) => {
                this.setState({
                    associatedCollectionsByTrackId: {
                        ...associatedCollectionsByTrackId,
                        [learningAssetId]: [...learningAssetStatus.associatedCollections]
                    }
                });
            });
        }
        return null;
    };

    updateCompanyExclusiveCollectionLicenses = (pathwayContent = []) => {
        const { collectionsById, currentValues, changeFormValue } = this.props;

        const existingLicensesById = (currentValues.collectionLicenses || []).reduce((acc, curr) => {
            acc[curr.collectionId] = curr;
            return acc;
        }, {});

        const contentLength = (pathwayContent || []).length;
        let currentContentItem;
        const collectionsToAdd = [];
        let learningAssetsToAdd = [];
        for (let i = 0; i < contentLength; i += 1) {
            currentContentItem = pathwayContent[i] && collectionsById[pathwayContent[i].id];
            if (
                currentContentItem &&
                (currentContentItem.subscriptionType === LICENSE_TYPES.EXCLUSIVE_COMPANY_LICENSE ||
                    (currentValues.lockUnlicensed &&
                        currentContentItem.subscriptionType === LICENSE_TYPES.AVAILABLE_FOR_ALL))
            ) {
                if (!(currentContentItem._id in existingLicensesById)) {
                    // add collection
                    collectionsToAdd.push({
                        collectionId: currentContentItem._id,
                        collectionName: currentContentItem.name,
                        licensedDateTime: moment.utc()
                    });

                    // add corresponding learning asset license
                    learningAssetsToAdd = learningAssetsToAdd.concat(currentContentItem.contentIds);
                }
            }
        }

        if (collectionsToAdd.length) {
            changeFormValue({
                field: 'collectionLicenses',
                value: (currentValues.collectionLicenses || []).concat(collectionsToAdd)
            });
            if (learningAssetsToAdd.length) {
                this.updateCompanyExclusiveLearningAssetLicenses(learningAssetsToAdd);
            }
        }
    };

    updatePathwayLicenseRemovalForPathwayOptions = (pathwayId) => {
        const { changeFormValue, currentValues } = this.props;
        const newPathwayOptions = [...currentValues.pathwayOptions];
        const pathIdx = newPathwayOptions.findIndex((path) => path.id === pathwayId);

        if (pathIdx > -1) {
            newPathwayOptions.splice(pathIdx, 1);
        }

        changeFormValue({
            field: 'pathwayOptions',
            value: newPathwayOptions
        });
    };

    getLearningAssetName = (learningAssetId) => get(this.props, `learningAssetsById[${learningAssetId}].name`, '');

    getLicensedDate = (member) => get(this.props, `company.${member}.licensedDateTime`, null);

    _renderAdminSections = () => {
        const {
            company,
            currentValues,
            availableCollections,
            availableLearningAssets,
            collectionsById,
            changeFormValue,
            loggedInUser,
            isVerbAdmin,
            isAccountOwner,
            isCompanyAdmin,
            isInstructionalAdmin
        } = this.props;
        const {
            selectedLearningAssetId,
            selectedLearningAsset,
            associatedCollectionsByTrackId,
            trackLicensesByLicencedCollectionId
        } = this.state;
        const hiddenAndRecommendedCollectionOptions = this.getCollectionOptionsForCompany(availableCollections);
        const defaultCollectionOptions =
            isVerbAdmin || isInstructionalAdmin
                ? this.getDefaultOptions(availableCollections)
                : hiddenAndRecommendedCollectionOptions;

        return (
            <div className={'company-collection-section'}>
                {isVerbAdmin || isInstructionalAdmin ? (
                    <>
                        <CollectionLicensesSection
                            trackLicensesByLicencedCollectionId={trackLicensesByLicencedCollectionId}
                            collectionsById={collectionsById}
                            availableCollections={this.getLicenseOptions(availableCollections)}
                            changeFormValue={changeFormValue}
                            updateCompanyExclusiveLearningAssetLicenses={
                                this.updateCompanyExclusiveLearningAssetLicenses
                            }
                            getLicensedDate={this.getLicensedDate}
                            currentValues={currentValues}
                        />
                        <br />
                    </>
                ) : null}
                <br />
                <RecommendedCollectionsSection
                    changeFormValue={changeFormValue}
                    recommendedCollectionOptions={hiddenAndRecommendedCollectionOptions}
                    currentValues={currentValues}
                    loggedInUser={loggedInUser}
                    isVerbAdmin={isVerbAdmin}
                    isAccountOwner={isAccountOwner}
                />
                <br />
                <br />
                {isVerbAdmin || isInstructionalAdmin ? (
                    <>
                        <div className={'learning-asset-licenses'}>
                            <label className={'form-label'}>Learning Asset Licenses</label>
                            <FieldArray
                                name={'trackLicenses'}
                                company={company}
                                associatedCollectionsByTrackId={associatedCollectionsByTrackId}
                                availableLearningAssets={availableLearningAssets}
                                selectedLearningAssetId={selectedLearningAssetId}
                                selectedLearningAsset={selectedLearningAsset}
                                getLicensedDate={this.getLicensedDate}
                                getLearningAssetName={this.getLearningAssetName}
                                handleLearningAssetChange={this._handleLearningAssetChange}
                                currentValues={currentValues}
                                collectionLicensesByCollectionId={keyBy(
                                    currentValues.collectionLicenses,
                                    'collectionId'
                                )}
                                handleAssociatedCollectionsFetching={this.handleAssociatedCollectionsFetching}
                                component={LearningAssetLicensesTable}
                            />
                        </div>
                        <br />
                        <HiddenLearningAssetsSection
                            changeFormValue={changeFormValue}
                            hiddenLearningAssetOptions={this.getHiddenAssetOptionsForCompany(availableLearningAssets)}
                            currentValues={currentValues}
                            loggedInUser={loggedInUser}
                            isVerbAdmin={isVerbAdmin}
                            isAccountOwner={isAccountOwner}
                        />
                    </>
                ) : null}
            </div>
        );
    };

    render() {
        const { submitting, handleSubmit, onCancel, error } = this.props;

        return (
            <div className={'company-content-form'}>
                <form className={'companyForm'}>
                    {this._renderAdminSections()}
                    <div className={'col-12 formButtons'}>
                        <VerbButton
                            fontAwesomeIcon={'fa-save'}
                            textId={'BUTTON_SAVE'}
                            defaultMessage={'SAVE'}
                            onClick={(values) => {
                                this.setState({ formSubmitted: true });
                                handleSubmit(values);
                            }}
                            disabled={submitting}
                        />
                    </div>
                    <FormFeedback failMsg={error} className={'col-12'} {...this.props} />
                </form>
            </div>
        );
    }
}

export const CompanyContentFormComponent = CompanyContentForm;

const getCompanyValues = getFormValues('CompanyContent');
export const makeMapStateToProps = () => {
    const getFilteredLearningAssetsFromState = makeGetFilteredObjectsFromState('learningAssets');
    const getCollectionsFromStateById = makeGetObjectsFromStateById('collections');
    const getPathwaysFromStateById = makeGetObjectsFromStateById('pathways');

    const mapStateToProps = (state, ownProps) => {
        const {
            collections,
            learningAssets,
            pathways,
            auth: { isVerbAdmin, isAccountOwner, loggedInUserId, loggedInUser, isCompanyAdmin, isInstructionalAdmin }
        } = state;
        const {
            trackLicenses,
            collectionLicenses,
            defaultCollections,
            lockUnlicensed = false,
            recommendedCollections,
            hiddenCollections,
            pathwayLicenses,
            customDiscover = {
                categories: []
            },
            hiddenLearningAssets,
            pathwaysEnabled = false,
            pathwayOptions = []
        } = ownProps.company;

        const availableCollections = getCollectionsFromStateById(state).filter((collection) => collection.isActive);

        return {
            dirty: isDirty('CompanyContent'),
            initialValues: {
                trackLicenses,
                collectionLicenses,
                defaultCollections,
                lockUnlicensed,
                recommendedCollections,
                hiddenCollections,
                customDiscover,
                hiddenLearningAssets,
                collectionSectionName: '',
                pathwayLicenses,
                pathwayOptions
            },
            currentValues: getCompanyValues(state) || {},
            learningAssetsById: learningAssets.byId,
            collectionsById: collections.byId,
            pathwaysById: pathways.byId,
            availableLearningAssets: getFilteredLearningAssetsFromState(
                state,
                (asset) => asset.subscriptionType !== 'NotAvailable' && asset.isActive === true
            ),
            availablePathways: getPathwaysFromStateById(state),
            availableCollections,
            isVerbAdmin,
            isCompanyAdmin,
            isInstructionalAdmin,
            isAccountOwner,
            loggedInUserId,
            loggedInUser,
            pathwaysEnabled
        };
    };

    return mapStateToProps;
};

const mapDispatchToProps = (dispatch) => ({
    getManyLearningAssets: (params) => dispatch(getManyLearningAssets(params)),
    putCompany: (params) => dispatch(putCompany(params)),
    getManyCollections: (params) => dispatch(getManyCollections(params)),
    changeFormValue: (params) => dispatch(change('CompanyContent', params.field, params.value)),
    getLearningAssetStatus: (params) => getLearningAssetStatus(params),
    getManyLearningAssetsAssociatedCollections: (pathsContext, params) => getManyLearningAssetsAssociatedCollections(pathsContext, params),
    getManyAdminPathways: (params) => dispatch(getManyAdminPathways(params))
});

const CompanyContentFormValidation = {
    form: 'CompanyContent',
    destroyOnUnmount: false,
    enableReinitialize: true
};

export default compose(
    connect(makeMapStateToProps, mapDispatchToProps),
    reduxForm(CompanyContentFormValidation)
)(CompanyContentForm);
